import {
  ArrowPathIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ArrowUpTrayIcon } from '@heroicons/react/24/solid';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onUploadClick: () => void;
  onRefreshClick: () => void;
  onRecheckSyncClick?: () => void;
  onStatsClick?: () => void;
  onOrganizeFilesClick?: () => void;
  onDeleteAllFlagged?: () => void;
  isRefreshing?: boolean;
  isRecheckingSync?: boolean;
  flaggedAuditCount?: number;
}

export const SearchBar = ({
  searchQuery,
  setSearchQuery,
  onUploadClick,
  onRefreshClick,
  onRecheckSyncClick,
  onStatsClick,
  onOrganizeFilesClick,
  onDeleteAllFlagged,
  isRefreshing = false,
  isRecheckingSync = false,
  flaggedAuditCount = 0,
}: SearchBarProps) => {
  return (
    <div className="flex items-center gap-2 px-2 py-1">
      {/* Compact search input */}
      <div className="relative w-40">
        <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          name="search"
          placeholder="Search..."
          aria-label="Search files"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full h-6 pl-7 pr-2 text-xs bg-muted/40 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 placeholder:text-muted-foreground/60"
        />
      </div>

      {/* Action buttons - right next to search */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onUploadClick}
          className="h-6 px-2 text-[11px] font-medium"
        >
          <ArrowUpTrayIcon className="h-3 w-3 mr-1" />
          Upload
        </Button>

        {onOrganizeFilesClick && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onOrganizeFilesClick}
                className="h-6 px-2 text-[11px] font-medium"
              >
                <SparklesIcon className="h-3 w-3 mr-1" />
                Organize
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              AI-powered file organization
            </TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefreshClick}
              disabled={isRefreshing}
              className="h-6 w-6 p-0"
            >
              <ArrowPathIcon
                className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Refresh
          </TooltipContent>
        </Tooltip>

        {onRecheckSyncClick && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onRecheckSyncClick}
                disabled={isRecheckingSync}
                className="h-6 w-6 p-0"
              >
                <CloudArrowUpIcon
                  className={`h-3 w-3 ${isRecheckingSync ? 'animate-pulse' : ''}`}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Recheck Document Sync
            </TooltipContent>
          </Tooltip>
        )}

        {onStatsClick && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onStatsClick}
                className="h-6 w-6 p-0"
              >
                <ChartBarIcon className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Artifact Statistics
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Flagged audit count pill - clickable to delete all flagged */}
      {flaggedAuditCount > 0 && (
        <button
          type="button"
          onClick={onDeleteAllFlagged}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium whitespace-nowrap hover:bg-amber-200 transition-colors cursor-pointer"
          title="Delete all flagged files"
        >
          <ExclamationTriangleIcon className="h-3 w-3" />
          {flaggedAuditCount} flagged
        </button>
      )}
    </div>
  );
};

export default SearchBar;
